import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { FOUNDERS, FOUNDERS_SECTION } from '@/data/about';
import { SectionHeader } from '../SectionHeader';
import { Reveal } from '../Reveal';
import { cn } from '@/lib/utils';

const FounderCard = ({ founder, index }) => {
  const [expanded, setExpanded] = useState(false);
  const reduce = useReducedMotion();
  const bioId = `founder-bio-${founder.id}`;

  return (
    <Reveal delay={index * 0.1} className="h-full">
      <article
        data-testid={`founder-card-${founder.id}`}
        className="flex h-full flex-col items-center rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm transition-shadow duration-300 hover:shadow-card sm:p-10"
      >
        <img
          src={founder.photo.src}
          alt={founder.photo.alt}
          width="360"
          height="360"
          loading="lazy"
          data-testid={`founder-photo-${founder.id}`}
          className="h-36 w-36 rounded-full object-cover ring-4 ring-gold-100 ring-offset-2 sm:h-44 sm:w-44"
        />
        <h3 className="mt-6 text-xl font-extrabold tracking-tight text-navy-900">{founder.name}</h3>
        <p className="mt-1.5 text-xs font-bold uppercase tracking-[0.16em] text-gold-700">{founder.role}</p>
        <div className="mt-5 max-w-md text-left">
          <p className="text-sm leading-relaxed text-slate-600 sm:text-base">{founder.summary}</p>
          <AnimatePresence initial={false}>
            {expanded && (
              <motion.div
                key="full-bio"
                id={bioId}
                initial={reduce ? false : { opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={reduce ? undefined : { opacity: 0, height: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
              >
                <div className="space-y-4 pt-4">
                  {founder.fullBio.map((p) => (
                    <p key={p.slice(0, 24)} className="text-sm leading-relaxed text-slate-600 sm:text-base">
                      {p}
                    </p>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={bioId}
          data-testid={`founder-read-more-${founder.id}`}
          className="group mt-6 inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2.5 text-sm font-bold text-navy-900 transition-colors hover:border-navy-900 hover:bg-navy-50"
        >
          {expanded ? 'Show less' : 'Read full profile'}
          <ChevronDown
            className={cn('h-4 w-4 transition-transform duration-200', expanded && 'rotate-180')}
            aria-hidden="true"
          />
        </button>
      </article>
    </Reveal>
  );
};

export const Founders = () => (
  <section
    id="founders"
    aria-labelledby="founders-heading"
    data-testid="founders-section"
    className="bg-white py-20 sm:py-28"
  >
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionHeader
        index="02"
        eyebrow={FOUNDERS_SECTION.eyebrow}
        title={FOUNDERS_SECTION.heading}
        description={FOUNDERS_SECTION.description}
        testid="founders-header"
      />
      <div className="mx-auto mt-14 grid max-w-4xl gap-8 md:grid-cols-2">
        {FOUNDERS.map((founder, i) => (
          <FounderCard key={founder.id} founder={founder} index={i} />
        ))}
      </div>
    </div>
  </section>
);
